// Catalog.ID Client — Registry Key Publication Consent Modal
// ============================================================================
//
// Modal shown when the registry module asks cld-id to publish a new
// `registry_signing_key` claim under the user's account. Surfaces:
//   - the requester (always "registry" today, but rendered from the envelope)
//   - the key's user-supplied label
//   - the SHA-512 fingerprint (short + full on hover) so a vigilant user can
//     verify it matches what registry's UI shows
//   - the registry module-identity fingerprint (machineFingerprint), so the
//     user can confirm it matches the binary they installed
//
// Returns one of two decisions:
//   'approve' — proceed with the four-stage publish flow
//   'deny'    — reject; nothing is created
//
// No 'session'/'always' policy slot — registry key publication is a high-stakes
// one-shot operation. Each new key requires explicit consent.
// ============================================================================

import type { Shell } from '@catalog-shell';
import { createFieldGroup, createFormRow, createElement } from '@catalog-shell';
import type { RegistryRequest } from './registry-types';

export type RegistryKeyConsentDecision = 'approve' | 'deny';

export function showRegistryKeyConsent(
    shell: Shell,
    request: RegistryRequest,
    /**
     * The logged-in account the claim will be created under. Shown
     * prominently: the claim binds to WHOEVER approves — a consent approved
     * under the wrong login silently files the machine key's claim on that
     * account, unusably for the machine's real owner (C7 finding). There is
     * no server-side binding between the requesting vault and a CID account
     * to check against, so surfacing the owner is the guard.
     */
    owningCid: string,
): Promise<RegistryKeyConsentDecision> {
    return new Promise<RegistryKeyConsentDecision>((resolve) => {
        let resolved = false;
        const resolveOnce = (d: RegistryKeyConsentDecision): void => {
            if (resolved) return;
            resolved = true;
            handle.close();
            resolve(d);
        };

        const container = createElement('div', { className: 'cs-entity-view cs-modal-body' });

        container.appendChild(createElement('h2', {
            className: 'cs-modal-title',
            textContent: 'Publish a Registry signing key?',
        }));

        // Owning account — FIRST, before any key detail: the claim is created
        // under the account approving this dialog. If the machine key belongs
        // to a different account, the right move is Deny + re-login.
        const accountGroup = createFieldGroup({ title: 'Publishing under account' });
        const accountRow = createFormRow({ label: 'Account' });
        accountRow.content.appendChild(createElement('code', {
            className: 'cs-field-view-text',
            textContent: owningCid,
        }));
        accountGroup.body.appendChild(accountRow.element);
        const accountNote = createElement('div', { className: 'cs-inline-warn' });
        accountNote.appendChild(createElement('strong', {
            textContent: `The key claim will belong to ${owningCid}. `,
        }));
        accountNote.appendChild(document.createTextNode(
            'If this machine’s key should belong to a different account, '
            + 'deny this request, log in as that account, and publish again.',
        ));
        accountGroup.body.appendChild(accountNote);
        container.appendChild(accountGroup.element);

        // Requester
        const requesterGroup = createFieldGroup({ title: 'Requester' });
        const moduleRow = createFormRow({ label: 'Module' });
        moduleRow.content.appendChild(createElement('div', {
            className: 'cs-field-view-text',
            textContent: request.requesterId,
        }));
        requesterGroup.body.appendChild(moduleRow.element);

        if (request.machineFingerprint) {
            const machineRow = createFormRow({ label: 'Machine identity' });
            const code = createElement('code', {
                textContent: request.machineFingerprint.slice(0, 16) + '…',
                attributes: { title: request.machineFingerprint },
            }) as HTMLElement;
            machineRow.content.appendChild(code);
            requesterGroup.body.appendChild(machineRow.element);
        }
        container.appendChild(requesterGroup.element);

        // Key being published
        const keyGroup = createFieldGroup({ title: 'Key being published' });

        if (request.label) {
            const labelRow = createFormRow({ label: 'Label' });
            labelRow.content.appendChild(createElement('div', {
                className: 'cs-field-view-text',
                textContent: request.label,
            }));
            keyGroup.body.appendChild(labelRow.element);
        }

        if (request.fingerprint) {
            const fpRow = createFormRow({ label: 'Fingerprint' });
            const code = createElement('code', {
                textContent: request.fingerprint.slice(0, 24) + '…',
                attributes: { title: request.fingerprint },
            }) as HTMLElement;
            fpRow.content.appendChild(code);
            keyGroup.body.appendChild(fpRow.element);
        }

        const algoRow = createFormRow({ label: 'Algorithm' });
        algoRow.content.appendChild(createElement('div', {
            className: 'cs-field-view-text',
            textContent: 'ML-DSA-65 (FIPS 204)',
        }));
        keyGroup.body.appendChild(algoRow.element);

        container.appendChild(keyGroup.element);

        // Warning banner — publication makes the user's catalog.id resolvable
        // from any Registry attribution claim signed by this key.
        //
        // Audit H4: built via DOM construction (createElement), not innerHTML
        // string templating. The previous form spliced an `escapeHtml(...)`
        // call on a hard-coded literal — pointless escaping, but the wider
        // pattern (innerHTML + concatenation) is the kind of thing that
        // grows insecure under maintenance. The C2 fix at the dispatcher
        // also guarantees `requesterId === 'cpr'` here, so the previous
        // conditional that left an empty `<code></code>` is now removed.
        const warn = createElement('div', { className: 'cs-inline-warn' });
        warn.appendChild(createElement('strong', {
            textContent: 'Approving publishes the public half of this key',
        }));
        warn.appendChild(document.createTextNode(' as a '));
        warn.appendChild(createElement('code', { textContent: 'registry_signing_key' }));
        warn.appendChild(document.createTextNode(
            ' claim on your catalog.id account, then submits it to operators for review. ' +
            'Anyone can subsequently match Registry attribution claims signed by this key ' +
            'to your username (',
        ));
        warn.appendChild(createElement('code', {
            textContent: 'see Registry Whitepaper §16.2a',
        }));
        warn.appendChild(document.createTextNode(
            '). Your private key never leaves the registry vault.',
        ));
        container.appendChild(warn);

        const actions = createElement('div', { className: 'cs-form-actions' });

        const btnDeny = createElement('button', {
            className: 'cs-btn cs-btn--no',
            textContent: 'Deny',
        }) as HTMLButtonElement;
        btnDeny.addEventListener('click', () => resolveOnce('deny'));
        actions.appendChild(btnDeny);

        const btnApprove = createElement('button', {
            className: 'cs-btn cs-btn--yes',
            textContent: 'Publish',
        }) as HTMLButtonElement;
        btnApprove.addEventListener('click', () => resolveOnce('approve'));
        actions.appendChild(btnApprove);

        container.appendChild(actions);

        const handle = shell.showModal(container, {
            // 'normal' (400px) is too tight for a multi-paragraph warning
            // followed by Deny / Publish actions — bumped to 'wide' (560px)
            // for breathing room. Matches the registry-side modal width
            // convention.
            size: 'wide',
            dismissOnOverlayClick: false,
            dismissOnEscape: false,
            onClose: () => resolveOnce('deny'),
        });
    });
}
